import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import Input from "../../components/reusable-input/Input";
import Button from "../../components/reusable-button/Button";
import logo from "../../asstets/images/vcu_logo.png";
import { login } from "../../apis/authApis";
import { EyeOffIcon, EyeOpenIcon } from "../../icons/reactIcons";
import { fetchAdminPermissions } from "../../redux/slices/permissionsSlice";
import { useAppDispatch } from "../../redux/store/hooks";
import { toast } from "react-toastify";
import { isEmpty, isValidEmail } from "../../utils/validation";

interface Props {
  setIsAuthenticated: (auth: boolean) => void;
}

const Login: React.FC<Props> = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isEmpty(email) || isEmpty(password)) {
      toast.error("Enter email and password");
      return;
    }

    if (!isValidEmail(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      setLoading(true);

      const res = await login({ email: email.trim(), password });

      const { details, accessToken, refreshToken } = res.data;

      // ✅ Store auth data
      localStorage.setItem("isAuthenticated", "true");
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);

      localStorage.setItem(
        "adminDetail",
        JSON.stringify({
          id: details.id,
          email: details.email,
          role: details.role.name,
          name: details.name,
        }),
      );

      if (res?.status === true) {
        toast.success("User logged in successfully");
      }

      setIsAuthenticated(true);
      navigate("/dashboard", { replace: true });
      dispatch(fetchAdminPermissions());
    } catch (error: any) {
      console.error("Login error:", error);
      toast.error(error?.response?.data?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleSAMLLogin = () => {
    window.location.href = `${process.env.REACT_APP_SHIBBOLETH_LOGIN_URL}/sso/login`;
  };

  return (
    <div className="login-container">
      {/* Left side with gradient & logo */}
      <div className="login-left">
        <img src={logo} alt="Logo" className="login-logo" />
      </div>

      {/* Right side with form */}
      <div className="login-right">
        <div className="login-right-header">
          <h1>VCU Admin Portal</h1>
          <p>
            {" "}
            Admins can log in and access the portal by entering their email ID
            and password.
          </p>
        </div>
        <form className="login-form" onSubmit={handleLogin}>
          <Input
            type="email"
            label="Email"
            placeholder="youremail@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            type="password"
            label="Password"
            value={password}
            placeholder="☆ ☆ ☆ ☆ ☆ ☆ ☆ ☆ ☆"
            onChange={(e) => setPassword(e.target.value)}
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword((prev) => !prev)}
            endIcon={showPassword ? <EyeOpenIcon /> : <EyeOffIcon />}
          />

          <div className="login-button">
            <div className="login-actions-container">
              <Button
                text={loading ? "Signing in..." : "Sign in"}
                type="submit"
                loading={loading}
              />

              <span className="side-or">or</span>

              <button
                type="button"
                className="saml-button"
                onClick={handleSAMLLogin}
              >
                <svg
                  className="saml-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
                </svg>
                Login With Shibboleth
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
